import { For, Show, createMemo, createSignal, type JSX } from "solid-js";
import type { AdminApi } from "../../api/client";
import { errorMessage, isRefreshRequired } from "../../api/errors";
import { ConfirmAction } from "../../components/ConfirmAction";
import { CopyText } from "../../components/CopyText";
import { Modal } from "../../components/Modal";
import { PanelState } from "../../components/PanelState";
import { StatusBadge } from "../../components/StatusBadge";
import type { GatewayKey, GatewayKeyState, KeyLimits, Relay } from "../../model";
import type { AsyncPanel } from "../../state/async";
import { formatDate, formatNumber } from "../../utils/format";

type KeyDialog =
  | { kind: "create" }
  | { kind: "secret"; label: string; bearer: string }
  | { kind: "manage"; keyID: string }
  | { kind: "state"; keyID: string; state: GatewayKeyState }
  | null;

interface KeyPanelProps {
  api: AdminApi;
  panel: AsyncPanel<GatewayKey[]>;
  relays: Relay[];
  notify: (tone: "success" | "error" | "info", message: string) => void;
}

function keyTone(state: GatewayKeyState): "good" | "warning" | "danger" {
  if (state === "active") return "good";
  if (state === "disabled") return "warning";
  return "danger";
}

function keyStateLabel(state: GatewayKeyState): string {
  return { active: "Active", disabled: "Disabled", revoked: "Revoked" }[state];
}

function limitLabel(value: number | null): string {
  return value === null ? "不限制" : formatNumber(value);
}

function parseLimit(value: string): number | null | "invalid" {
  if (value.trim() === "") return null;
  const numberValue = Number(value);
  if (!Number.isInteger(numberValue) || numberValue <= 0 || numberValue > 4_294_967_295) {
    return "invalid";
  }
  return numberValue;
}

function toggleRelayID(current: string[], relayID: string): string[] {
  return current.includes(relayID)
    ? current.filter((id) => id !== relayID)
    : [...current, relayID];
}

export function KeyPanel(props: KeyPanelProps): JSX.Element {
  const [dialog, setDialog] = createSignal<KeyDialog>(null);
  const keys = () => props.panel.state().data ?? [];
  const keyByID = (keyID: string): GatewayKey | null => keys().find((key) => key.id === keyID) ?? null;

  async function mutate(action: () => Promise<void>, success: string): Promise<boolean> {
    try {
      await action();
      const refreshed = await props.panel.refresh();
      if (refreshed === null) {
        props.notify(
          "error",
          "操作可能已完成，但 Key 列表刷新失败；请在列表刷新成功后再继续编辑。",
        );
        setDialog(null);
        return false;
      }
      props.notify("success", success);
      return true;
    } catch (error) {
      props.notify("error", errorMessage(error));
      if (isRefreshRequired(error)) await props.panel.refresh();
      return false;
    }
  }

  async function setEligibleRelays(key: GatewayKey, desiredRelayIDs: string[]): Promise<boolean> {
    const current = new Set(key.eligibleRelayIDs);
    const desired = new Set(desiredRelayIDs);
    const additions = desiredRelayIDs.filter((relayID) => !current.has(relayID));
    const removals = key.eligibleRelayIDs.filter((relayID) => !desired.has(relayID));
    if (additions.length === 0 && removals.length === 0) return true;

    const added = await Promise.allSettled(
      additions.map((relayID) => props.api.keys.addEligibleRelay(key.id, relayID)),
    );
    const addFailures = added.filter((result) => result.status === "rejected").length;
    let removeFailures = 0;
    if (addFailures === 0) {
      const removed = await Promise.allSettled(
        removals.map((relayID) => props.api.keys.removeEligibleRelay(key.id, relayID)),
      );
      removeFailures = removed.filter((result) => result.status === "rejected").length;
    }

    const refreshed = await props.panel.refresh();
    if (refreshed === null) {
      props.notify(
        "error",
        "部分 Relay 操作可能已完成，但 Key 列表刷新失败；请在列表刷新成功后再继续编辑。",
      );
      setDialog(null);
      return false;
    }
    if (addFailures > 0 || removeFailures > 0) {
      props.notify(
        "error",
        addFailures > 0
          ? `有 ${addFailures} 个 Relay 添加失败；为避免缩小候选集，本次未执行移除。当前状态已刷新。`
          : `有 ${removeFailures} 个 Relay 移除失败，当前状态已刷新。`,
      );
      return false;
    }
    props.notify(
      "success",
      `Eligible Relay Set 已更新：添加 ${additions.length} 个，移除 ${removals.length} 个。`,
    );
    return true;
  }

  function stateImpacts(key: GatewayKey, state: GatewayKeyState): string[] {
    if (state === "revoked") {
      return [
        "使用该 bearer 的所有新请求都会被拒绝。",
        "后端当前允许再次修改状态，因此 UI 不会宣称不可逆。",
        "后端没有 bearer 查询、轮换或删除接口。",
      ];
    }
    if (state === "disabled") {
      return ["该 Gateway Key 将暂时无法通过认证。", "现有 Session Binding 不会因此删除。"];
    }
    return ["该 Gateway Key 将恢复认证和请求准入。", `当前 Eligible Relay 数量：${key.eligibleRelayIDs.length}。`];
  }

  return (
    <section class="bento bento--table" aria-labelledby="keys-heading">
      <header class="bento__header">
        <div>
          <p class="eyebrow">Gateway Keys</p>
          <h2 id="keys-heading">Gateway Key 管理</h2>
        </div>
        <button type="button" class="button button--primary" onClick={() => setDialog({ kind: "create" })}>
          创建 Gateway Key
        </button>
      </header>
      <div class="table-scroll">
        <table>
          <thead>
            <tr>
              <th>Gateway Key</th>
              <th>状态</th>
              <th>Public ID</th>
              <th>Concurrency</th>
              <th>RPM</th>
              <th>Eligible Relays</th>
              <th>更新时间</th>
              <th><span class="sr-only">操作</span></th>
            </tr>
          </thead>
          <tbody>
            <For each={keys()} fallback={<tr><td colSpan={8}><div class="empty-state"><strong>暂无 Gateway Key</strong><span>创建 Key 后 bearer 只显示一次。</span></div></td></tr>}>
              {(key) => (
                <tr>
                  <td>
                    <button type="button" class="entity-link" onClick={() => setDialog({ kind: "manage", keyID: key.id })}>
                      <strong>{key.label}</strong><code>{key.id}</code>
                    </button>
                  </td>
                  <td><StatusBadge label={keyStateLabel(key.state)} tone={keyTone(key.state)} /></td>
                  <td><CopyText value={key.publicID} label="Public ID" onError={() => props.notify("error", "复制失败。")}/></td>
                  <td>{limitLabel(key.limits.concurrency)}</td>
                  <td>{limitLabel(key.limits.rpm)}</td>
                  <td>
                    <span class={`eligible-count ${key.eligibleRelayIDs.length === 0 ? "eligible-count--empty" : ""}`}>
                      {key.eligibleRelayIDs.length} 个
                    </span>
                  </td>
                  <td>{formatDate(key.updatedAtMS)}</td>
                  <td><button type="button" class="button button--ghost button--small" onClick={() => setDialog({ kind: "manage", keyID: key.id })}>管理</button></td>
                </tr>
              )}
            </For>
          </tbody>
        </table>
      </div>
      <PanelState state={props.panel.state()} onRetry={() => void props.panel.refresh()} />

      <Show when={dialog()?.kind === "create"}>
        <KeyCreateDialog
          api={props.api}
          relays={props.relays}
          onClose={() => setDialog(null)}
          onCreated={(created) => {
            setDialog({ kind: "secret", label: created.key.label, bearer: created.bearer });
            props.panel.replace([...keys().filter((key) => key.id !== created.key.id), created.key]);
          }}
          notify={props.notify}
        />
      </Show>
      <Show keyed when={dialog()?.kind === "secret" ? (dialog() as Extract<KeyDialog, { kind: "secret" }>) : null}>
        {(value) => <SecretOnceDialog label={value.label} bearer={value.bearer} onClose={() => setDialog(null)} notify={props.notify} />}
      </Show>
      <Show keyed when={dialog()?.kind === "manage" ? (dialog() as Extract<KeyDialog, { kind: "manage" }>) : null}>
        {(value) => (
          <Show keyed when={keyByID(value.keyID)}>
            {(key) => (
              <KeyManageDialog
                key={key}
                relays={props.relays}
                onClose={() => setDialog(null)}
                onSetState={(state) => setDialog({ kind: "state", keyID: key.id, state })}
                onSetLimits={(limits) => mutate(() => props.api.keys.setLimits(key.id, limits), "Gateway Key limits 已更新。")}
                onSetEligibleRelays={(relayIDs) => setEligibleRelays(key, relayIDs)}
              />
            )}
          </Show>
        )}
      </Show>
      <Show keyed when={dialog()?.kind === "state" ? (dialog() as Extract<KeyDialog, { kind: "state" }>) : null}>
        {(value) => (
          <Show keyed when={keyByID(value.keyID)}>
            {(key) => (
              <ConfirmAction
                title={`将 Gateway Key 设为 ${keyStateLabel(value.state)}`}
                target={key.label}
                confirmLabel={`确认设为 ${keyStateLabel(value.state)}`}
                impacts={stateImpacts(key, value.state)}
                strong={value.state === "revoked"}
                tone={value.state === "revoked" ? "danger" : "primary"}
                onCancel={() => setDialog({ kind: "manage", keyID: key.id })}
                onConfirm={() => mutate(() => props.api.keys.setState(key.id, value.state), `Gateway Key 已设为 ${keyStateLabel(value.state)}。`)}
              />
            )}
          </Show>
        )}
      </Show>
    </section>
  );
}

function KeyCreateDialog(props: {
  api: AdminApi;
  relays: Relay[];
  onClose: () => void;
  onCreated: (created: { key: GatewayKey; bearer: string }) => void;
  notify: KeyPanelProps["notify"];
}): JSX.Element {
  const [label, setLabel] = createSignal("");
  const [eligible, setEligible] = createSignal<string[]>([]);
  const [concurrency, setConcurrency] = createSignal("");
  const [rpm, setRPM] = createSignal("");
  const [formError, setFormError] = createSignal<string | null>(null);
  const [busy, setBusy] = createSignal(false);

  function toggle(relayID: string): void {
    setEligible((current) => toggleRelayID(current, relayID));
  }

  async function submit(): Promise<void> {
    const parsedConcurrency = parseLimit(concurrency());
    const parsedRPM = parseLimit(rpm());
    if (label().trim() === "" || [...label()].length > 80) {
      setFormError("Label 必须包含 1 到 80 个字符。");
      return;
    }
    if (eligible().length === 0) {
      setFormError("至少选择一个 Eligible Relay。");
      return;
    }
    if (parsedConcurrency === "invalid" || parsedRPM === "invalid") {
      setFormError("Limit 必须为空或正 uint32。");
      return;
    }
    setBusy(true);
    setFormError(null);
    try {
      const created = await props.api.keys.create({
        label: label(),
        eligibleRelayIDs: eligible(),
        limits: { concurrency: parsedConcurrency, rpm: parsedRPM },
      });
      props.notify("success", "Gateway Key 已创建。Bearer 只显示这一次。");
      props.onCreated(created);
    } catch (error) {
      setFormError(errorMessage(error));
      props.notify("error", errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title="创建 Gateway Key" eyebrow="Bearer 只显示一次" onClose={props.onClose} busy={busy()} footer={<><button class="button button--ghost" type="button" onClick={props.onClose}>取消</button><button class="button button--primary" type="button" disabled={busy()} onClick={() => void submit()}>{busy() ? "正在创建…" : "创建 Gateway Key"}</button></>}>
      <div class="form-grid">
        <label class="field field--wide"><span>Label</span><input maxlength={80} value={label()} onInput={(event) => setLabel(event.currentTarget.value)} /></label>
        <label class="field"><span>Concurrency limit</span><input type="number" min="1" max="4294967295" placeholder="不限制" value={concurrency()} onInput={(event) => setConcurrency(event.currentTarget.value)} /></label>
        <label class="field"><span>RPM limit</span><input type="number" min="1" max="4294967295" placeholder="不限制" value={rpm()} onInput={(event) => setRPM(event.currentTarget.value)} /></label>
      </div>
      <fieldset class="choice-fieldset">
        <legend>Eligible Relay Set</legend>
        <Show when={props.relays.length > 0} fallback={<p>没有可选择的 Relay。</p>}>
          <For each={props.relays}>
            {(relay) => (
              <label class="choice-row">
                <input type="checkbox" checked={eligible().includes(relay.id)} onChange={() => toggle(relay.id)} />
                <span><strong>{relay.displayName}</strong><small>{relay.id}</small></span>
                <StatusBadge label={relay.state} tone={relay.state === "active" ? "good" : "neutral"} />
              </label>
            )}
          </For>
        </Show>
      </fieldset>
      <Show when={eligible().length > 0 && !props.relays.some((relay) => relay.state === "active" && eligible().includes(relay.id))}>
        <p class="callout callout--warning">当前选择中没有 active Relay；后端允许创建，但新会话没有可用候选节点。</p>
      </Show>
      <Show when={formError()}>{(message) => <p class="callout callout--danger">{message()}</p>}</Show>
    </Modal>
  );
}

function SecretOnceDialog(props: { label: string; bearer: string; onClose: () => void; notify: KeyPanelProps["notify"] }): JSX.Element {
  return (
    <Modal title="保存 Gateway Key Bearer" eyebrow="仅此一次" onClose={props.onClose} footer={<button type="button" class="button button--primary" onClick={props.onClose}>我已保存，关闭</button>}>
      <p class="callout callout--warning">关闭后无法从 UI 或后端再次查询。请立即保存到受控的 Secret Manager。</p>
      <div class="secret-once">
        <span>{props.label}</span>
        <CopyText value={props.bearer} label="Bearer" secret onError={() => props.notify("error", "复制失败，请手动选择保存。")}/>
      </div>
    </Modal>
  );
}

function KeyManageDialog(props: {
  key: GatewayKey;
  relays: Relay[];
  onClose: () => void;
  onSetState: (state: GatewayKeyState) => void;
  onSetLimits: (limits: KeyLimits) => Promise<boolean>;
  onSetEligibleRelays: (relayIDs: string[]) => Promise<boolean>;
}): JSX.Element {
  const [concurrency, setConcurrency] = createSignal(props.key.limits.concurrency?.toString() ?? "");
  const [rpm, setRPM] = createSignal(props.key.limits.rpm?.toString() ?? "");
  const [eligibleRelayIDs, setEligibleRelayIDs] = createSignal([...props.key.eligibleRelayIDs]);
  const [error, setError] = createSignal<string | null>(null);
  const [busy, setBusy] = createSignal<string | null>(null);
  const relayChanges = createMemo(() => {
    const current = new Set(props.key.eligibleRelayIDs);
    const desired = new Set(eligibleRelayIDs());
    return {
      additions: eligibleRelayIDs().filter((relayID) => !current.has(relayID)).length,
      removals: props.key.eligibleRelayIDs.filter((relayID) => !desired.has(relayID)).length,
    };
  });
  const relaySetDirty = () => relayChanges().additions > 0 || relayChanges().removals > 0;

  async function saveLimits(): Promise<void> {
    const parsedConcurrency = parseLimit(concurrency());
    const parsedRPM = parseLimit(rpm());
    if (parsedConcurrency === "invalid" || parsedRPM === "invalid") {
      setError("Limit 必须为空或正 uint32。");
      return;
    }
    setBusy("limits");
    if (await props.onSetLimits({ concurrency: parsedConcurrency, rpm: parsedRPM })) setError(null);
    setBusy(null);
  }

  function toggleRelay(relayID: string): void {
    setEligibleRelayIDs((current) => toggleRelayID(current, relayID));
  }

  async function saveRelaySet(): Promise<void> {
    if (!relaySetDirty()) return;
    setBusy("relays");
    await props.onSetEligibleRelays(eligibleRelayIDs());
    setBusy(null);
  }

  return (
    <Modal title={props.key.label} eyebrow="Gateway Key 管理" onClose={props.onClose} busy={busy() !== null} size="wide">
      <div class="detail-grid">
        <section>
          <h3>Identity</h3>
          <dl class="detail-list">
            <dt>Internal ID</dt><dd><code>{props.key.id}</code></dd>
            <dt>Public ID</dt><dd><code>{props.key.publicID}</code></dd>
            <dt>State</dt><dd><StatusBadge label={keyStateLabel(props.key.state)} tone={keyTone(props.key.state)} /></dd>
            <dt>Auth version</dt><dd>{formatNumber(props.key.authVersion)}</dd>
            <dt>Updated</dt><dd>{formatDate(props.key.updatedAtMS)}</dd>
          </dl>
          <div class="button-row">
            <For each={(["active", "disabled", "revoked"] as GatewayKeyState[]).filter((state) => state !== props.key.state)}>
              {(state) => <button type="button" class={`button ${state === "revoked" ? "button--danger-subtle" : "button--ghost"}`} onClick={() => props.onSetState(state)}>设为 {keyStateLabel(state)}</button>}
            </For>
          </div>
        </section>
        <section>
          <h3>Admission Limits</h3>
          <div class="form-grid">
            <label class="field"><span>Concurrency</span><input type="number" min="1" placeholder="不限制" value={concurrency()} onInput={(event) => setConcurrency(event.currentTarget.value)} /></label>
            <label class="field"><span>RPM</span><input type="number" min="1" placeholder="不限制" value={rpm()} onInput={(event) => setRPM(event.currentTarget.value)} /></label>
          </div>
          <p class="field-note">空值表示不启用对应 Gateway Key 限制。</p>
          <button type="button" class="button button--primary" disabled={busy() !== null} onClick={() => void saveLimits()}>{busy() === "limits" ? "保存中…" : "保存 Limits"}</button>
          <Show when={error()}>{(message) => <p class="callout callout--danger">{message()}</p>}</Show>
        </section>
      </div>
      <section class="detail-section">
        <h3>Eligible Relay Set</h3>
        <p class="field-note">
          可一次选择多个 Relay 后统一保存。移除 Relay 后，默认情况下相关 Session Binding 的下一次请求返回
          <code>402 session_unavailable</code> 并保留旧 Binding；只有 Gateway 显式开启兼容重绑时才会选择新 Relay。
        </p>
        <div class="eligibility-list">
          <For each={props.relays}>
            {(relay) => {
              const included = () => eligibleRelayIDs().includes(relay.id);
              return (
                <label class="eligibility-row">
                  <div><strong>{relay.displayName}</strong><code>{relay.id}</code></div>
                  <StatusBadge label={relay.state} tone={relay.state === "active" ? "good" : "neutral"} />
                  <input
                    type="checkbox"
                    aria-label={`${relay.displayName} Eligible Relay`}
                    checked={included()}
                    disabled={busy() !== null}
                    onChange={() => toggleRelay(relay.id)}
                  />
                </label>
              );
            }}
          </For>
        </div>
        <Show when={eligibleRelayIDs().length === 0}>
          <p class="callout callout--danger">至少保留一个 Eligible Relay。</p>
        </Show>
        <Show when={eligibleRelayIDs().length > 0 && !props.relays.some((relay) => relay.state === "active" && eligibleRelayIDs().includes(relay.id))}>
          <p class="callout callout--warning">当前选择中没有 active Relay；新会话没有可用候选节点。</p>
        </Show>
        <Show when={relaySetDirty()}>
          <p class="callout callout--warning">
            将添加 {relayChanges().additions} 个，移除 {relayChanges().removals} 个。添加成功后才会执行移除。
          </p>
        </Show>
        <div class="button-row relay-set-actions">
          <button
            type="button"
            class="button button--primary"
            disabled={busy() !== null || !relaySetDirty() || eligibleRelayIDs().length === 0}
            onClick={() => void saveRelaySet()}
          >
            {busy() === "relays" ? "保存中…" : "保存 Relay Set"}
          </button>
        </div>
      </section>
    </Modal>
  );
}
